import { useEffect, useMemo, useState } from 'react';

import { faGrid } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { CircularProgress } from '@mui/material';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useInView } from 'react-intersection-observer';
import { useHistory } from 'react-router';

import { useShouldHideHuddle } from '../../modules/patientList/useShouldHideHuddle';
import { PracticeCard } from '../../modules/simplifiedInsights/PracticeCard';
import { SearchPracticeInput } from '../../modules/simplifiedInsights/SearchPracticesInput';
import { useGetPractices } from '../../react-query/useGetPractices';
import routesConstants from '../../routes/routes.constants';
import { useDebounce } from '../../utils/hooks/useDebounce';

interface PracticesDialogProps {
  children?: React.ReactNode;
  title?: 'huddle' | 'claims';
}

export const PracticesDialog: React.FC<PracticesDialogProps> = ({
  children,
  title,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { debouncedValue: debouncedSearch } = useDebounce(search);
  const history = useHistory();
  const { ref: bottomOfPageRef, inView: isBottomPracticeInView } = useInView();

  const {
    data: practices,
    fetchNextPage,
    isLoading,
  } = useGetPractices({
    practice: {
      name: debouncedSearch,
    },
    cleanClaims: title === 'claims',
    hideInSwitchboard: true,
  });
  const hideHuddle = useShouldHideHuddle();

  const { nextCursor } = practices?.pages[practices.pages.length - 1] || {};
  useEffect(() => {
    if (nextCursor && isBottomPracticeInView && practices?.pages.length) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    nextCursor,
    practices?.pages.length,
    isBottomPracticeInView,
  ]);

  const practiceCards = useMemo(() => {
    const practicesToUse = practices?.pages?.flatMap(
      (practicePage) => practicePage.data,
    );

    return practicesToUse?.map((practice) => (
      <PracticeCard
        practice={practice}
        key={practice.id}
        title={title || 'huddle'}
        onClose={() => setIsOpen(false)}
      />
    ));
  }, [practices, title, setIsOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children}
      <DialogContent className="vai-max-w-[832px] vai-max-h-[510px] vai-p-6 vai-gap-4 vai-overflow-y-auto vai-z-[9999]">
        <DialogHeader className="vai-flex vai-items-center vai-spacing/1-5">
          <DialogTitle className="vai-flex vai-items-center vai-gap-2">
            <FontAwesomeIcon icon={faGrid} />
            <p className="vai-font-inter vai-font-semibold vai-text-xl">
              <Trans>Select a location</Trans>
            </p>
          </DialogTitle>
          <DialogDescription className="vai-font-inter vai-font-normal vai-text-sm">
            {hideHuddle ? (
              <Trans>Practice view is location specific</Trans>
            ) : title === 'claims' ||
              history.location.pathname.includes(routesConstants.CLAIMS) ? (
              <Trans>Claims view is location specific</Trans>
            ) : (
              <Trans>Daily Dashboard view is location specific</Trans>
            )}
          </DialogDescription>
          <SearchPracticeInput search={search} setSearch={setSearch} />
        </DialogHeader>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <div className="vai-grid vai-grid-cols-5 vai-gap-4">
            {practiceCards}
            {practiceCards && <div ref={bottomOfPageRef} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
